<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <h1 class="title">Taxi drivers</h1>
      <app-cells>
        <app-button @click.native="$modal.show('driver-form')">Add taxi driver</app-button>
        <app-input
          @input.native="onUsersSearch"
          v-model="search"
          class="input--200"
          type="search"
          placeholder="Search by name"
          name="search"
        />
        <app-select
          @input="onChangeFilter"
          v-model="cars_filter"
          :reduce="item => item.id"
          :options="cars_options"
          label="name"
          placeholder="Car model"
          class="select--200"
        />
      </app-cells>
      <v-client-table
          :data="table.items"
          :columns="table.columns"
          :options="table.options"
          class="table-default"
      >
        <template #h__first_name><div class="table-default__left">Name</div></template>
        <template #h__car><div class="table-default__left">Car</div></template>
        <template #first_name="{ row }">
          <div class="table-default__left">
            <router-link :to="{ name: 'taxi-drivers-detail', params: { id: row.id } }" class="table-default__link-border">
              {{ row.first_name }} {{ row.last_name || '' }}
            </router-link>
          </div>
        </template>
        <template #total_ordered="{ row }">{{ row.total_ordered.toLocaleString() }} TSh</template>
        <template #total_unpaid="{ row }">{{ row.total_unpaid.toLocaleString() }} TSh</template>
        <template #total_paid="{ row }">{{ row.total_paid.toLocaleString() }} TSh</template>
        <template #communication="{ row }">
          <div class="table-default__controls">
            <button
                v-tooltip="{content: row.phone, placement: 'top-start', offset: 10}"
                @click="onCopyContactLink(row.phone)"
                class="table-default__activity table-default__activity--phone"
                :class="{'table-default__activity--active': row.phone}"
            >
            </button>
            <a
                :href="`https://t.me/${row.phone}`"
                target="_blank"
                class="table-default__activity table-default__activity--tg"
                :class="{'table-default__activity--active': row.phone_telegram}"
            >
            </a>
            <a
                :href="`https://wa.me/${row.phone}`"
                target="_blank"
                class="table-default__activity table-default__activity--wa"
                :class="{'table-default__activity--active': row.phone_whatsapp}"
            >
            </a>
          </div>
        </template>
        <template #communication_other="{ row }">
          <div class="table-default__controls">
            <button
                v-tooltip="{content: row.additional_phone, placement: 'top-start', offset: 10}"
                @click="onCopyContactLink(row.phone)"
                class="table-default__activity table-default__activity--phone"
                :class="{'table-default__activity--active': row.additional_phone}"
            >
            </button>
            <a
                :href="`https://t.me/${row.additional_phone}`"
                target="_blank"
                class="table-default__activity table-default__activity--tg"
                :class="{'table-default__activity--active': row.additional_phone_telegram}"
            >
            </a>
            <a
                :href="`https://wa.me/${row.additional_phone}`"
                target="_blank"
                class="table-default__activity table-default__activity--wa"
                :class="{'table-default__activity--active': row.additional_phone_whatsapp}"
            >
            </a>
          </div>
        </template>
        <template #car="{ row }">
          <div class="table-default__left">{{ row.car_model.name }} ({{ row.plate_number }})</div>
        </template>
      </v-client-table>
    </template>
    <modal
        name="driver-form"
        :width="700"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('driver-form')" class="popup__close" type="button"></button>
        <div class="popup__title">Adding taxi driver</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-grid>
              <template #item-1>
                <app-form-group label="First name" label-for="first_name" required>
                  <app-input
                      v-model="form.first_name"
                      @change.native="$v.form.first_name.$touch()"
                      :error="$v.form.first_name.$error"
                      id="first_name"
                      placeholder="First name"
                      name="first_name"
                  />
                  <template #error>
                    <div v-if="$v.form.first_name.$dirty && !$v.form.first_name.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Last name" label-for="last_name">
                  <app-input
                      v-model="form.last_name"
                      id="last_name"
                      placeholder="Last name"
                      name="last_name"
                  />
                </app-form-group>
                <app-form-group label="Phone number">
                  <app-phone
                      v-model="form.phone"
                      placeholder="+7 499 113 28 00"
                  />
                </app-form-group>
                <app-form-group label="Other communications">
                  <app-cells :indent="false">
                    <app-checkbox v-model="form.phone_telegram" label="Telegram" :readonly="!form.phone" />
                    <app-checkbox v-model="form.phone_whatsapp" label="Whatsapp" :readonly="!form.phone" />
                  </app-cells>
                </app-form-group>
              </template>
              <template #item-2>
                <app-form-group label="Car model" required>
                  <app-select
                    v-model="form.car_model"
                    :reduce="item => item.id"
                    :options="cars_options"
                    :clearable="false"
                    placeholder="Car model"
                    :error="$v.form.car_model.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.car_model.$dirty && !$v.form.car_model.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Plate number" label-for="plate_number" required>
                  <app-input
                      v-model="form.plate_number"
                      @change.native="$v.form.plate_number.$touch()"
                      :error="$v.form.plate_number.$error"
                      id="plate_number"
                      placeholder="Plate number"
                      name="plate_number"
                  />
                  <template #error>
                    <div v-if="$v.form.plate_number.$dirty && !$v.form.plate_number.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Phone number (additional)">
                  <app-phone
                      v-model="form.additional_phone"
                      placeholder="+7 499 113 28 00"
                  />
                </app-form-group>
                <app-form-group label="Other communications">
                  <app-cells :indent="false">
                    <app-checkbox v-model="form.additional_phone_telegram" label="Telegram" :readonly="!form.additional_phone" />
                    <app-checkbox v-model="form.additional_phone_whatsapp" label="Whatsapp" :readonly="!form.additional_phone" />
                  </app-cells>
                </app-form-group>
              </template>
            </app-grid>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form.$error" type="submit" ref="submit">Add taxi driver</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { getTransfersTaxiDriversList, getCarManagementCarModels, postTransfersTaxiDrivers } from '@/http'
import debounce from 'lodash/debounce'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'TaxiDriversList',
  data() {
    return {
      is_loaded: false,
      table: {
        items: [],
        columns: ['first_name', 'total_ordered', 'total_unpaid', 'total_paid', 'communication', 'communication_other', 'car'],
        options: {
          headings: {
            'first_name': 'Name',
            'total_ordered': 'Total ordered',
            'total_unpaid': 'Total unpaid',
            'total_paid': 'Total paid',
            'communication': 'Main phone',
            'communication_other': 'Additional phone',
            'car': 'Car',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_limit: 20,
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      cars_options: [],
      cars_filter: '',
      search: '',
      form: {}
    }
  },
  validations: {
    form: {
      first_name: { required },
      car_model: { required },
      plate_number: { required },
    }
  },
  created() {
    this.fetchItems()
    getCarManagementCarModels().then(res => { this.cars_options = res.data })
  },
  methods: {
    fetchItems() {
      getTransfersTaxiDriversList(
        { page: this.pagination.page, page_size: 20, search: this.search, car_model: this.search ? '' : this.cars_filter }
      )
        .then(res => {
          this.table.items = res.data.results
          this.pagination.pages = res.data.pages
          this.pagination.count = res.data.count
          this.is_loaded = true
        })
    },
    onCopyContactLink(link) {
      navigator.clipboard.writeText(link)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Copied to clipboard',
          })
        })
        .catch(() => { console.error('Something went wrong') })
    },
    onUsersSearch() {
      this.searchUsers(this)
    },
    searchUsers: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
    onChangeFilter() {
      this.search = ''
      this.pagination.page = 1
      this.fetchItems()
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      postTransfersTaxiDrivers(this.form)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Taxi driver created',
          })
          this.fetchItems()
          this.$modal.hide('driver-form')
          this.form = {}
          this.$v.form.$reset()
        })
        .catch(() => { this.$refs.submit.preload = false })
    },
  }
}
</script>
